import React, { useState } from 'react';
import {
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  Flex,
  IconBox,
  ListItem,
  Table,
} from '@/components/ui';
import { FormSelectorTrigger } from './FormSelectorTrigger';
import { FormSelectorDropdown } from './FormSelectorDropdown';
import { FormSelectorFields } from './FormSelectorFields';
import { FormSelectorHint } from './FormSelectorHint';
import type {
  ColumnMapping,
  FileToFormMapping,
  SelectedFile,
  StudyForm,
} from '../types';
import { FormIcon } from '@/pages/data-import/components';
import { IconAlertTriangle, IconChevronDown } from '@tabler/icons-react';

interface FormSelectorProps {
  selectedForm: StudyForm | null;
  availableForms: StudyForm[];
  mapping?: FileToFormMapping;
  file?: SelectedFile;
  onFormSelect: (formId: string | number, nestedPath?: string) => void;
  onClearMapping?: () => void;
  className?: string;
  showFieldMatching?: boolean;
  disableColumns?: boolean;
  children?: React.ReactNode;
  hoveredElement?: {
    type: 'column' | 'field' | 'metadata';
    name: string;
  } | null;
  onElementHover?: (
    element: { type: 'column' | 'field' | 'metadata'; name: string } | null
  ) => void;
  getElementOpacity?: (
    elementType: 'column' | 'field' | 'metadata',
    elementName: string
  ) => number;
  onFieldClick?: (fieldDataPoint: string) => void;
  columnMappings?: ColumnMapping[];
  fileId?: string;
  useNewSelector?: boolean;
  onFieldColumnMappingChange?: (
    fileId: string,
    columnName: string,
    mappingType: 'formField' | 'metadata' | 'ignored',
    targetField?: string
  ) => void;
  useName?: boolean; // Show form.name instead of displayName
}

export const FormSelector: React.FC<FormSelectorProps> = ({
  selectedForm,
  availableForms,
  mapping,
  file,
  onFormSelect,
  disableColumns,
  className = 'form-info-block',
  children,
  hoveredElement,
  onElementHover,
  getElementOpacity,
  onFieldClick,
  columnMappings,
  fileId,
  useNewSelector = false,
  onFieldColumnMappingChange,
  useName = false,
}) => {
  void className;
  const [formSelectorOpen, setFormSelectorOpen] = useState(false);

  const handleFormSelect = (formId: string, nestedPath: string) => {
    onFormSelect(parseInt(formId), nestedPath);
    setFormSelectorOpen(false);
  };

  const handleDropdownOpenChange = (open: boolean) => {
    setFormSelectorOpen(open);
  };

  return (
    <Table.Cell>
      <Flex direction="column" gap="2" className="mapping-block" flexGrow="1">
        <DropdownMenu
          open={formSelectorOpen}
          onOpenChange={handleDropdownOpenChange}
        >
          <DropdownMenuTrigger>
            <Button variant="surface" asChild className="dropdown">
              <ListItem
                startIcon={
                  selectedForm ? (
                    <FormIcon
                      form={selectedForm}
                      availableForms={availableForms}
                    />
                  ) : (
                    <IconBox color="warning" size="small">
                      <IconAlertTriangle />
                    </IconBox>
                  )
                }
                endIcon={<IconChevronDown />}
              >
                <FormSelectorTrigger
                  selectedForm={selectedForm}
                  availableForms={availableForms}
                  mapping={mapping}
                  file={file}
                  useName={useName}
                />
              </ListItem>
            </Button>
          </DropdownMenuTrigger>
          <FormSelectorDropdown
            availableForms={availableForms}
            file={file}
            selectedForm={selectedForm}
            nestedPath={mapping?.nested_path}
            onFormSelect={handleFormSelect}
            useName={useName}
          />
        </DropdownMenu>

        {selectedForm && (
          <FormSelectorHint
            selectedForm={selectedForm}
            availableForms={availableForms}
            mapping={mapping}
            useName={useName}
          />
        )}

        {children}

        {/* Form Fields Preview */}
        {selectedForm && (
          <FormSelectorFields
            selectedForm={selectedForm}
            file={file}
            fileId={fileId}
            columnMappings={columnMappings}
            disableColumns={disableColumns}
            useNewSelector={useNewSelector}
            hoveredElement={hoveredElement}
            onElementHover={onElementHover}
            getElementOpacity={getElementOpacity}
            onFieldClick={onFieldClick}
            onFieldColumnMappingChange={onFieldColumnMappingChange}
          />
        )}
      </Flex>
    </Table.Cell>
  );
};

export default FormSelector;
